import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, Download, ExternalLink, FileUp, Files, Share2 } from "lucide-react";
import AttachVersionModal from "@/components/review/AttachVersionModal";
import CommentsPanel from "@/components/review/CommentsPanel";
import DraftStatusBadge from "@/components/review/DraftStatusBadge";
import PreviewCanvas from "@/components/review/PreviewCanvas";
import RenameReviewModal from "@/components/review/RenameReviewModal";
import ReviewActionsMenu from "@/components/review/ReviewActionsMenu";
import ShareReviewModal from "@/components/review/ShareReviewModal";
import RecordingViewer from "@/components/review/RecordingViewer";
import WebsitePreview from "@/components/review/WebsitePreview";
import { copyReviewFeedback, downloadReviewFeedback, type FeedbackScope } from "@/components/review/review-feedback-actions";
import type { PreviewPinDisplay } from "@/components/review/PinLayer";
import { commentAnchor, describeMutationError, pinNumbersForVersion, type CommentAnchor } from "@/components/review/review-model";
import PageHeader from "@/components/shell/PageHeader";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import EmptyState from "@/components/ui/EmptyState";
import Menu from "@/components/ui/Menu";
import MenuSelect from "@/components/ui/MenuSelect";
import { Page, PageBody } from "@/components/ui/Page";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, space, text } from "@/design/tokens.stylex";
import { showError, showSuccess } from "@/lib/toast";
import { useWindowTitleDetail } from "@/lib/window-title";
import { queryClient, trpc, trpcClient } from "@/lib/trpc";
import { useReloadForNewBuild } from "@/lib/stale-build";
const NARROW = "@media (max-width: 1023px)";
const styles = {
  page: {
    kZKoxP: "x5yr21d",
    kAzted: "x2lwn1j",
    $$css: true
  },
  back: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xsuxsxl",
    kMwMTN: "x1i6qc9y xfpavck",
    kybGjl: "x1hl2dhg",
    $$css: true
  },
  sourceName: {
    ks0D6T: "x15bk1ce",
    kMwMTN: "x1e3avs9",
    $$css: true
  },
  sourceLink: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xsuxsxl",
    $$css: true
  }
};
export const Route = createFileRoute("/_authenticated/$orgSlug/review/$draftId")({
  // Loader + useSuspenseQuery share one cache entry (same pattern as
  // $orgSlug.index.tsx). organization.viewer is already ensured by the parent
  // layout's loader, so the component's read below is cache-only.
  loader: ({
    params,
    context
  }) => context.queryClient.ensureQueryData(trpc.review.getDraft.queryOptions({
    draftId: params.draftId
  })),
  errorComponent: DraftLoadError,
  component: DraftPage
});
function DraftLoadError({
  error
}: {
  error: unknown;
}) {
  const {
    orgSlug
  } = Route.useParams();
  if (useReloadForNewBuild(error)) return null;
  return <Page>
      <PageHeader title="Review" />
      <PageBody>
        <EmptyState title="Could not open this review" action={<Link to="/$orgSlug/review" params={{
        orgSlug
      }} {...stylex.props(recipes.link)}>
              Back to reviews
            </Link>}>
          {error instanceof Error ? error.message : String(error)}
        </EmptyState>
      </PageBody>
    </Page>;
}
function DraftPage() {
  const {
    orgSlug,
    draftId
  } = Route.useParams();
  const navigate = useNavigate();
  const {
    data
  } = useSuspenseQuery(trpc.review.getDraft.queryOptions({
    draftId
  }));
  const {
    data: viewer
  } = useSuspenseQuery(trpc.organization.viewer.queryOptions());
  const {
    draft,
    versions,
    comments
  } = data;
  useWindowTitleDetail(draft.title);

  // null = follow the latest version (versions arrive newest-first).
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<CommentAnchor | null>(null);
  const [pendingCrop, setPendingCrop] = useState<string | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [videoTime, setVideoTime] = useState<number | null>(null);
  const [showAttach, setShowAttach] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const latestVersion = versions[0] ?? null;
  const selectedVersion = versions.find(version => version.id === selectedVersionId) ?? latestVersion;
  const archived = draft.status === "archived";
  const isWebReview = draft.kind === "web";

  // Version switch re-renders the preview and filters comments/approvals.
  const versionComments = selectedVersion ? comments.filter(comment => comment.versionId === selectedVersion.id) : [];
  const pinNumbers = pinNumbersForVersion(versionComments);
  const selectedComment = versionComments.find(comment => comment.id === selectedCommentId);
  const selectedCommentTime = selectedComment ? commentAnchor(selectedComment)?.timecode ?? null : null;
  const pins: PreviewPinDisplay[] = versionComments.flatMap(comment => {
    const anchor = commentAnchor(comment);
    const number = pinNumbers.get(comment.id);
    if (!anchor || number === undefined) return [];
    return [{
      commentId: comment.id,
      number,
      anchor,
      resolved: comment.status === "resolved",
      selected: selectedCommentId === comment.id
    }];
  });
  function clearPending() {
    setPendingAnchor(null);
    setPendingCrop(null);
  }
  function switchVersion(versionId: string) {
    setSelectedVersionId(versionId);
    clearPending();
    setSelectedCommentId(null);
    setVideoTime(null);
  }
  async function copyFeedback(scope: FeedbackScope) {
    try {
      await copyReviewFeedback(data, orgSlug, scope);
      showSuccess(scope === "all" ? "All comments copied" : "Open comments copied");
    } catch {
      showError("Could not copy. Use Download as file instead.");
    }
  }
  async function archiveDraft() {
    setConfirmArchive(false);
    setArchiving(true);
    try {
      await trpcClient.review.archiveDraft.mutate({
        draftId
      });
      await Promise.all([queryClient.invalidateQueries({
        queryKey: trpc.review.getDraft.queryKey({
          draftId
        })
      }), queryClient.invalidateQueries({
        queryKey: trpc.review.listDrafts.queryKey({
          orgSlug
        })
      })]);
      void navigate({
        to: "/$orgSlug/review",
        params: {
          orgSlug
        }
      });
    } catch (cause) {
      showError(describeMutationError(cause).message);
    } finally {
      setArchiving(false);
    }
  }
  const attachLabel = isWebReview ? "Add page" : "Attach version";
  const recording = selectedVersion?.recording ?? null;
  const webPreviewUrl = isWebReview && selectedVersion?.mimeType === "text/html" ? selectedVersion.pageUrl ?? draft.sourceUrl : null;
  return <Page xstyle={styles.page}>
      <PageHeader back={<Link to="/$orgSlug/review" params={{
      orgSlug
    }} aria-label="Back to all reviews" {...stylex.props(recipes.focusRing, styles.back)}>
            <ArrowLeft aria-hidden size={14} />
            Review
          </Link>} title={draft.title} context={
    // A recording names its page in the viewer's own page menu.
    selectedVersion && !selectedVersion.recording ? <SourceLine draft={draft} version={selectedVersion} /> : undefined} actions={<>
            {/* A recording's next version comes from the Mac app. */}
            {!archived && latestVersion && !latestVersion.recording && <Button icon={<FileUp size={14} />} onClick={() => setShowAttach(true)}>
                {attachLabel}
              </Button>}
            {latestVersion && <Button variant="primary" icon={<Share2 size={14} />} onClick={() => setShowShare(true)}>
                Share
              </Button>}
            {latestVersion && <Menu label="Copy" icon={<Copy size={14} />} aria-label="Copy comments" items={[{
        label: "Open comments, this version",
        icon: <Copy size={14} />,
        onSelect: () => void copyFeedback(selectedVersion ? {
          versionId: selectedVersion.id
        } : "all")
      }, {
        label: "All comments, every version",
        icon: <Files size={14} />,
        onSelect: () => void copyFeedback("all")
      }, {
        separator: true
      }, {
        label: "Download as file",
        icon: <Download size={14} />,
        onSelect: () => downloadReviewFeedback(data, orgSlug)
      }]} />}
            <ReviewActionsMenu onRename={() => setShowRename(true)} onShare={latestVersion ? undefined : () => setShowShare(true)} onArchive={() => setConfirmArchive(true)} showArchive={!archived} archiving={archiving} />
          </>} />

      <div className="x78zum5 x1q0g3np x1dh0ydc x98rzlu x2lwn1j x7be28s xb3r6kr xcay4t3 x178xt8z x13fuv20 x13hb0vi">
        <div {...{
        0: {
          className: "x98rzlu x15f271x xeuugli x2lwn1j x913imy xysyzu8 xze2s60 x19qxo9d"
        },
        1: {
          className: "x98rzlu x15f271x xeuugli x2lwn1j x913imy xysyzu8 x19qxo9d x1717udv"
        }
      }[!!Boolean(webPreviewUrl || recording) << 0]}>
          {recording ? <RecordingViewer recording={recording} title={draft.title} pins={pins} pendingAnchor={pendingAnchor} canComment={!archived} onPickElement={(anchor, crop) => {
          setPendingAnchor(anchor);
          setPendingCrop(crop);
          setSelectedCommentId(null);
        }} onClearPendingAnchor={clearPending} onSelectPin={commentId => setSelectedCommentId(current => current === commentId ? null : commentId)} /> : webPreviewUrl ? <WebsitePreview url={webPreviewUrl} title={draft.title} pins={pins} pendingAnchor={pendingAnchor} canComment={!archived} onPlacePin={anchor => {
          setPendingAnchor(anchor);
          setSelectedCommentId(null);
        }} onClearPendingAnchor={() => setPendingAnchor(null)} onSelectPin={commentId => setSelectedCommentId(current => current === commentId ? null : commentId)} /> : selectedVersion ? <div className="x36rmqc xvueqy4">
              <PreviewCanvas version={selectedVersion} pins={pins} pendingAnchor={pendingAnchor} canComment={!archived} onPlacePin={anchor => {
            setPendingAnchor({
              ...anchor,
              url: isWebReview ? selectedVersion.pageUrl ?? draft.sourceUrl ?? undefined : undefined
            });
            setSelectedCommentId(null);
          }} onSelectPin={commentId => setSelectedCommentId(current => current === commentId ? null : commentId)} seekTime={selectedCommentTime} onVideoTimeChange={setVideoTime} defaultScale={isWebReview ? "width" : "fit"} />
            </div> : <EmptyState centered icon={<FileUp size={24} />} title="No file attached yet" action={!archived && <Button variant="primary" icon={<FileUp size={14} />} onClick={() => setShowAttach(true)}>
                    {attachLabel}
                  </Button>}>
              {isWebReview ? "Record the site in the Growth Engine desktop app (Website review), or add a URL and its full-page screenshot." : "Attach the first version from Google Drive to start the review."}
            </EmptyState>}
        </div>

        {/* Mercury 05/12: status and version at the top, the conversation below
            with its composer pinned to the foot. */}
        <aside aria-label="Review" className="x78zum5 xdt5ytf x2lah0s x1cy8gbr x14shcbm x2lwn1j x1ywmeps x1h1f4i9 xe0pwq x1c1f6qm x19ypqd9 x1ffzt9r x972fbf xb0fqrj x13fuv20 x13hb0vi">
          <div className="x78zum5 xdt5ytf x166cprz x9end0a x1jbum4n xso031l x1q0q8m5 xaecqrk">
            <div className="x78zum5 x6s0dn4 x166cprz">
              <DraftStatusBadge status={draft.status} />
              {draft.publicShareEnabled ? <Badge tone="success">Shared</Badge> : <Badge>Private</Badge>}
            </div>
            <span className="x7x6yu3 xk50ysn x1i6qc9y">
              {isWebReview && !recording ? "Page capture" : "Version"}
            </span>
            <MenuSelect value={selectedVersion?.id ?? ""} onValueChange={switchVersion} disabled={versions.length === 0} ariaLabel="Version" fullWidth options={versions.length === 0 ? [{
            value: "",
            label: "No versions"
          }] : versions.map(version => ({
            value: version.id,
            label: version.recording ? `v${version.versionNumber} — ${version.fileName}` : isWebReview ? websiteCaptureLabel(version.pageUrl, version.versionNumber) : `v${version.versionNumber} — ${version.fileName}`
          }))} />
            {selectedVersion?.note && <p className="x1ghz6dp x7x6yu3 x1e3avs9">{selectedVersion.note}</p>}
          </div>

          <CommentsPanel draftId={draftId} orgSlug={orgSlug} version={selectedVersion} comments={versionComments} viewerId={viewer.userId} pinNumbers={pinNumbers} pendingAnchor={pendingAnchor} pendingCrop={pendingCrop} onClearPendingAnchor={clearPending} selectedCommentId={selectedCommentId} onSelectComment={id => setSelectedCommentId(id)} disabled={archived} automaticTimecode={selectedVersion?.mimeType.startsWith("video/") ? videoTime : null} />
        </aside>
      </div>

      {showAttach && <AttachVersionModal draftId={draftId} orgSlug={orgSlug} kind={draft.kind} defaultPageUrl={selectedVersion?.pageUrl ?? draft.sourceUrl} onClose={() => setShowAttach(false)} onAttached={switchVersion} />}
      {showRename && <RenameReviewModal draftId={draftId} title={draft.title} onClose={() => setShowRename(false)} />}
      {showShare && <ShareReviewModal draftId={draftId} enabled={draft.publicShareEnabled} onClose={() => setShowShare(false)} />}
      <ConfirmDialog open={confirmArchive} title="Archive this review?" message="Archived reviews stay readable but no longer accept comments or versions." confirmLabel="Archive" onConfirm={() => void archiveDraft()} onCancel={() => setConfirmArchive(false)} />
    </Page>;
}
function websiteCaptureLabel(pageUrl: string | null, versionNumber: number) {
  if (!pageUrl) return `Capture ${versionNumber}`;
  try {
    const url = new URL(pageUrl);
    const path = url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "");
    return `${url.hostname.replace(/^www\./, "")}${path} · capture ${versionNumber}`;
  } catch {
    return `Capture ${versionNumber}`;
  }
}
function SourceLine({
  draft,
  version
}: {
  draft: {
    kind: string;
    sourceUrl: string | null;
  };
  version: {
    versionNumber: number;
    pageUrl: string | null;
    fileName: string;
    driveWebUrl: string | null;
    sourceType: string;
    snapshotUrl: string | null;
  };
}) {
  const isWeb = draft.kind === "web";
  const pageUrl = version.pageUrl ?? draft.sourceUrl;
  return <span className="x78zum5 x1a02dak x6s0dn4 x1bclm3k x18cz73r xeuugli">
      <span {...stylex.props(styles.sourceName, recipes.truncate)}>
        {isWeb && pageUrl ? websiteCaptureLabel(pageUrl, version.versionNumber).replace(/ · capture \d+$/, "") : version.fileName}
      </span>
      {isWeb && pageUrl && <a href={pageUrl} target="_blank" rel="noreferrer" {...stylex.props(recipes.link, styles.sourceLink)}>
          <ExternalLink aria-hidden size={11} /> Open page
        </a>}
      {version.driveWebUrl && <a href={version.driveWebUrl} target="_blank" rel="noreferrer" {...stylex.props(recipes.link, styles.sourceLink)}>
          <ExternalLink aria-hidden size={11} /> {isWeb ? "Screenshot" : "Drive"}
        </a>}
      {version.snapshotUrl && <a href={version.sourceType === "public_drive" ? `${version.snapshotUrl}${version.snapshotUrl.includes("?") ? "&" : "?"}download=1` : version.snapshotUrl} download={version.fileName} {...stylex.props(recipes.link, styles.sourceLink)}>
          <Download aria-hidden size={11} /> Download
        </a>}
    </span>;
}